import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { logService } from '../lib/logService';
import type { FormResponse, ResponsesFilter, UpdateResponseInput } from '../types/forms';

// =====================================================
// HOOK PARA RESPOSTAS DE FORMULÁRIOS - ONEFORM
// =====================================================

export function useFormResponses(formId: string | undefined, filter?: ResponsesFilter) {
  const [responses, setResponses] = useState<FormResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchResponses = useCallback(async () => {
    if (!formId) {
      setResponses([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('form_responses')
        .select('*', { count: 'exact' })
        .eq('form_id', formId);

      // Aplicar filtros
      if (filter?.status && filter.status !== 'all') {
        query = query.eq('status', filter.status);
      }

      if (filter?.dateFrom) {
        query = query.gte('created_at', filter.dateFrom);
      }

      if (filter?.dateTo) {
        query = query.lte('created_at', filter.dateTo);
      }

      // Ordenação (score sorting is handled client-side)
      const sortBy = filter?.sortBy === 'score' ? 'created_at' : (filter?.sortBy || 'created_at');
      const sortOrder = filter?.sortOrder || 'desc';
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      const { data, error: fetchError, count } = await query;

      if (fetchError) {
        logService.error('responses.fetch', fetchError.message, { supabase_code: fetchError.code, form_id: formId });
        setError('Erro ao carregar respostas');
        return;
      }

      setResponses(data as FormResponse[]);
      setTotalCount(count || 0);
    } catch (err) {
      logService.error('responses.fetch', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: formId });
      setError('Erro inesperado ao carregar respostas');
    } finally {
      setLoading(false);
    }
  }, [formId, filter?.status, filter?.dateFrom, filter?.dateTo, filter?.sortBy, filter?.sortOrder]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  // Atualizar resposta (status, notas)
  const updateResponse = useCallback(async (
    responseId: string,
    updates: UpdateResponseInput
  ): Promise<{ response: FormResponse | null; error: string | null }> => {
    try {
      const { data, error: updateError } = await supabase
        .from('form_responses')
        .update(updates)
        .eq('id', responseId)
        .select()
        .single();

      if (updateError) {
        logService.error('responses.update', updateError.message, { supabase_code: updateError.code, response_id: responseId });
        return { response: null, error: 'Erro ao atualizar resposta' };
      }

      // Atualizar lista local
      setResponses(prev => prev.map(r => r.id === responseId ? (data as FormResponse) : r));

      return { response: data as FormResponse, error: null };
    } catch (err) {
      logService.error('responses.update', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, response_id: responseId });
      return { response: null, error: 'Erro inesperado ao atualizar' };
    }
  }, []);

  // Deletar resposta
  const deleteResponse = useCallback(async (responseId: string): Promise<{ error: string | null }> => {
    try {
      const { error: deleteError } = await supabase
        .from('form_responses')
        .delete()
        .eq('id', responseId);

      if (deleteError) {
        logService.error('responses.delete', deleteError.message, { supabase_code: deleteError.code, response_id: responseId });
        return { error: 'Erro ao deletar resposta' };
      }

      // Atualizar lista local
      setResponses(prev => prev.filter(r => r.id !== responseId));
      setTotalCount(prev => prev - 1);

      return { error: null };
    } catch (err) {
      logService.error('responses.delete', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, response_id: responseId });
      return { error: 'Erro inesperado ao deletar' };
    }
  }, []);

  // Client-side score filtering
  const scoreFilteredResponses = useMemo(() => {
    if (!filter?.scoreRange || !filter?.scoreVariableId) return responses;

    return responses.filter(r => {
      const varScore = r.scores?.[filter.scoreVariableId!];
      return varScore?.range?.label === filter.scoreRange;
    });
  }, [responses, filter?.scoreRange, filter?.scoreVariableId]);

  // Client-side score sorting
  const sortedResponses = useMemo(() => {
    if (filter?.sortBy !== 'score' || !filter?.scoreVariableId) {
      return scoreFilteredResponses;
    }

    return [...scoreFilteredResponses].sort((a, b) => {
      const aScore = a.scores?.[filter.scoreVariableId!]?.totalPoints ?? -1;
      const bScore = b.scores?.[filter.scoreVariableId!]?.totalPoints ?? -1;
      return filter.sortOrder === 'asc' ? aScore - bScore : bScore - aScore;
    });
  }, [scoreFilteredResponses, filter?.sortBy, filter?.sortOrder, filter?.scoreVariableId]);

  // Estatísticas
  const abandonedCutoff = Date.now() - 24 * 60 * 60 * 1000;
  const stats = {
    total: totalCount,
    new: responses.filter(r => r.status === 'new').length,
    inProgress: responses.filter(r => r.status === 'in_progress').length,
    completed: responses.filter(r => r.status === 'completed').length,
    abandoned: responses.filter(
      r => r.status === 'in_progress' && new Date(r.updated_at).getTime() < abandonedCutoff
    ).length,
  };

  return {
    responses: sortedResponses,
    loading,
    error,
    totalCount,
    stats,
    refetch: fetchResponses,
    updateResponse,
    deleteResponse,
  };
}

// =====================================================
// HOOK PARA UMA RESPOSTA ESPECÍFICA
// =====================================================

export function useFormResponse(responseId: string | undefined) {
  const [response, setResponse] = useState<FormResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchResponse = async () => {
      if (!responseId) {
        setResponse(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('form_responses')
          .select('*, form:forms(*)')
          .eq('id', responseId)
          .single();

        if (fetchError) {
          logService.error('responses.fetch', fetchError.message, { supabase_code: fetchError.code, response_id: responseId });
          setError('Resposta não encontrada');
          return;
        }

        setResponse(data as FormResponse);
      } catch (err) {
        logService.error('responses.fetch', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, response_id: responseId });
        setError('Erro ao carregar resposta');
      } finally {
        setLoading(false);
      }
    };

    fetchResponse();
  }, [responseId]);

  const updateResponse = useCallback(async (updates: UpdateResponseInput): Promise<{ error: string | null }> => {
    if (!responseId) return { error: 'ID da resposta não fornecido' };

    try {
      const { data, error: updateError } = await supabase
        .from('form_responses')
        .update(updates)
        .eq('id', responseId)
        .select('*, form:forms(*)')
        .single();

      if (updateError) {
        logService.error('responses.update', updateError.message, { supabase_code: updateError.code, response_id: responseId });
        return { error: 'Erro ao atualizar' };
      }

      setResponse(data as FormResponse);
      return { error: null };
    } catch (err) {
      logService.error('responses.update', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, response_id: responseId });
      return { error: 'Erro inesperado' };
    }
  }, [responseId]);

  return {
    response,
    loading,
    error,
    updateResponse,
  };
}
